<template>
  <t-dropdown ref="dropdown"
              trigger-mode="custom"
              :visibled.sync="currentVisibled"
              :disabled="currentDisabled"
              :position="position"
  >
    <div slot="trigger"
         ref="wrapper"
         v-clickoutside="$_onClickOutside"
         :class="selectClasses"
         :style="selectStyle"
         @click="$_onTriggerClick"
         @mouseenter="$_onTriggerMouseenter"
         @mouseleave="$_onTriggerMouseleave"
    >
      <div :class="triggerClasses">
        <template v-if="multiple === false">
          <span v-if="placeholderVisibled" :class="placeholderClasses">{{ placeholderText }}</span>
          <span v-if="!filterable && !placeholderVisibled" :class="selectedClasses">{{ singleSelected }}</span>
        </template>
        <template v-else>
          <ul v-if="multipleSelected.length > 0" class="list-inline">
            <li v-for="(item, index) in multipleSelected"
                v-show="!collapseTags || index === 0"
                :key="index"
                class="list-inline-item"
            >
              <t-tag>{{ item.label }} <t-icon icon="close-outline" class="tag__clear" @click.native.stop="$_onRemoveTagClick(item)"></t-icon></t-tag>
            </li>
            <li v-if="collapseTags && multipleSelected.length > 1" class="list-inline-item">
              <span class="tag tag--text">
                <span>+{{ multipleSelected.length - 1 }}</span>
              </span>
            </li>
          </ul>
          <span v-if="placeholderVisibled" :class="placeholderClasses">{{ placeholderText }}</span>
        </template>
        <input v-if="filterable"
               ref="input"
               v-model="filterValue"
               :class="inputClasses"
               :placeholder="placeholderText"
               :disabled="currentDisabled"
               type="text"
               @keydown="$_onInputKeydown"
               @keydown.delete="$_onInputDelete"
        />
        <span :class="iconClasses">
          <t-icon v-show="closeIconVisibled" icon="close-circle" class="cursor-pointer" @click.native.stop="$_onCloseIconClick"></t-icon>
          <t-icon v-show="arrowIconVisibled" icon="chevron-down-outline" data-ani-icon></t-icon>
        </span>
      </div>
    </div>
    <div slot="popper">
      <div v-show="notFoundVisibled"
           :class="tipsClasses"
      >{{ localeNotFoundText }}</div>
      <t-tree
        ref="tree"
        v-model="currentNode"
        :class="selectTreeClasses"
        :node-key="nodeKey"
        :multiple="multiple"
        :with-ancestors="withAncestors"
        :with-descendants="withDescendants"
        :lazy-loader="lazyLoader"
        :lazy-icon="lazyIcon"
        :node-data="nodeData"
        @select="$_onTreeSelect"
      ></t-tree>
      <div v-show="currentLoading"
           :class="tipsClasses"
      >{{ localeLoadingText }}</div>
    </div>
  </t-dropdown>
</template>
<script>
import { TTree } from '../tree'
import clickoutside from '../../directives/click-outside'
import { PREFIX_CLASS, CLASS_MAIN, PREFIX_ICON_CLASS } from '../../utils/constants'
import { FILTER_RESET, FILTER_NOT_MATCHED } from '../list/constants'
import Locale from '../../mixins/locale'
import Emitter from '../../mixins/emitter'
import { findComponentUpward } from '../../utils/assist'
import bus from '../../utils/bus'
const PREFIX_SELECT_CLASS = `${PREFIX_CLASS}select`

export default {
  name: 'TSelectTree',
  directives: { clickoutside },
  components: {
    TTree
  },
  mixins: [ Locale, Emitter ],
  props: {
    collapseTags: Boolean,
    width: {
      type: [String, Number],
      default: ''
    },
    withAncestors: {
      type: Boolean,
      default: false
    },
    withDescendants: {
      type: Boolean,
      default: false
    },
    value: {
      type: [String, Number, Array, Object],
      default: null
    },
    nodeData: {
      type: Array,
      default() {
        return []
      }
    },
    nodeKey: {
      type: String,
      default: 'id' // 将使用模型默认值
    },
    nodeValue: {
      type: String,
      default: 'value'
    },
    stripe: {
      type: Boolean,
      default: false
    },
    multiple: {
      type: Boolean,
      default: false
    },
    lazyLoader: {
      type: Function,
      default: null
    },
    lazyIcon: {
      type: String,
      default: '' // 将使用模型默认值
    },
    disabled: {
      type: Boolean,
      default: false
    },
    clearable: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    loadingText: {
      type: String,
      default: ''
    },
    size: {
      type: String,
      default: '',
      validator(value) {
        return ['', 'lg', 'sm'].includes(value)
      }
    },
    placeholder: {
      type: String,
      default: ''
    },
    notFoundText: {
      type: String,
      default: ''
    },
    position: {
      type: String,
      validator(value) {
        /*
         * 有以下值：
         * 'top',
         * 'top-start',
         * 'top-end',
         * 'bottom',
         * 'bottom-start',
         * 'bottom-end',
         * https://popper.js.org/popper-documentation.html#Popper.placements
         */
        return ['top', 'top-start', 'top-end', 'bottom', 'bottom-start', 'bottom-end'].includes(value)
      },
      default: 'bottom-start'
    },
    autoComplete: {
      type: Boolean,
      default: false
    },
    filterable: {
      type: Boolean,
      default: false
    },
    filter: {
      type: Function,
      default: null
    },
    visibled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      treeFilterValue: '',
      loadDelayTimer: null,
      currentLoading: false,
      matched: FILTER_RESET,
      inside: false,
      filterValue: '',
      fromChild: false,
      currentClearable: false,
      currentDisabled: false,
      currentValue: null,
      currentNode: [],
      currentVisibled: false,
      arrowIconVisibled: true,
      focused: false,
      initFilter: true,
      currentItemIndex: -1
    }
  },
  computed: {
    listVisibled() {
      let load = this.$listeners.load
      if (this.notFoundVisibled === true) {
        return false
      }
      if (load && this.currentLoading === true) {
        return false
      }
      return true
    },
    localeLoadingText () {
      if (!this.loadingText) {
        return this.t('t.select.loading')
      } else {
        return this.loadingText
      }
    },
    localeNotFoundText() {
      if (!this.notFoundText) {
        return this.t('t.select.noMatch')
      } else {
        return this.notFoundText
      }
    },
    tipsClasses() {
      return [`${PREFIX_SELECT_CLASS}__tips`, 'cursor-text']
    },
    notFoundVisibled() {
      return this.filterable && this.currentLoading !== true && this.matched === FILTER_NOT_MATCHED
    },
    inputClasses() {
      return `${PREFIX_SELECT_CLASS}__input`
    },
    closeIconVisibled() {
      return this.currentDisabled === false &&
             this.multiple === false &&
             this.currentClearable &&
             this.placeholderVisibled === false &&
             this.arrowIconVisibled === false
    },
    arrowIconClasses() {
      return `${CLASS_MAIN} ${PREFIX_ICON_CLASS}chevron-down-outline`
    },
    closeIconClasses() {
      return [`${CLASS_MAIN} ${PREFIX_ICON_CLASS}close-circle`, 'cursor-pointer']
    },
    iconClasses() {
      return `${PREFIX_SELECT_CLASS}__selection-icon`
    },
    selectedClasses() {
      return `${PREFIX_SELECT_CLASS}__selected-value`
    },
    placeholderClasses() {
      return `${PREFIX_SELECT_CLASS}__placeholder`
    },
    placeholderText() {
      if (this.placeholder === '') {
        return this.t('t.select.placeholder')
      } else {
        return this.placeholder
      }
    },
    selectTreeClasses() {
      return `${PREFIX_SELECT_CLASS}-tree__content`
    },
    placeholderVisibled() {
      if (this.filterable === false) {
        if (this.currentValue === null || this.currentValue === undefined) {
          return true
        } else if (Array.isArray(this.currentValue)) {
          return this.currentValue.length === 0
        } else if (typeof this.currentValue === 'object' &&
          !this.currentValue.label &&
          !this.currentValue.value) {
          return true
        }
      }
      return false
    },
    // 单选模式选中项的标签文本
    singleSelected() {
      if (this.currentValue === null || this.currentValue === undefined) {
        return ''
      } else if (typeof this.currentValue === 'object') {
        return this.currentValue.label
      } else {
        return this.currentValue
      }
    },
    // 多选模式选中项的标签文本
    multipleSelected() {
      if (Array.isArray(this.currentValue) === true) {
        return this.currentValue.concat()
      } else {
        return []
      }
    },
    selectStyle() {
      return {
        width: `${this.width}`
      }
    },
    selectClasses () {
      return [
        `${PREFIX_SELECT_CLASS}`,
        {
          [`${PREFIX_SELECT_CLASS}--disabled`]: this.currentDisabled,
          [`${PREFIX_SELECT_CLASS}--open`]: this.currentVisibled,
          [`${PREFIX_SELECT_CLASS}--${this.size}`]: this.size,
          [`${PREFIX_SELECT_CLASS}--multiple`]: this.multiple,
          [`${PREFIX_SELECT_CLASS}--focus`]: this.focused,
          [`${PREFIX_SELECT_CLASS}--select-none`]: !this.filterable
        }
      ]
    },
    triggerClasses() {
      return {
        [`${PREFIX_SELECT_CLASS}__selection`]: true,
        [`input--disabled`]: this.currentDisabled
      }
    },
    prop () {
      let parent = this.$parent
      while (parent && parent.$options && parent.$options.name !== 'TFormItem') {
        parent = parent.$parent
      }
      return (parent && parent.prop) || ''
    }
  },
  watch: {
    loading(val) {
      if (this.filterable) {
        this.currentLoading = val
      }
    },
    filterValue(val) {
      if (this.filterable) {
        if (this.$listeners.load) {
          if (this.currentLoading) {
            if (val === '') {
              this.$emit('update:loading', false)
            } else {
              this.$emit('load', val)
            }
          } else {
            this.$emit('update:loading', true)
            this.$emit('load', val)
          }
          this.treeFilterValue = ''
          this.currentVisibled = true
        } else {
          if (!this.initFilter) {
            this.currentVisibled = false
            return
          }
          this.$_changeCurrentVisibled(true)
          this.treeFilterValue = val
        }
      } else {
        this.treeFilterValue = val
      }
    },
    value(val) {
      if (this.inside === false) { // 只有从父组件中修改value才走下面的逻辑
        this.currentValue = this.$_normalizeValue(val)
      } else {
        this.inside = false
      }
    },
    currentNode(val) {
      if (val.length > 0) {
        const nodes = this.$_treeNodes(val)
        this.currentValue = this.multiple ? nodes.map(node => ({
          id: node.id,
          value: node.data,
          label: node.label
        })) : {
          id: nodes[0].id,
          value: nodes[0].data,
          label: nodes[0].label
        }
      }
    },
    currentValue(val) {
      if (this.multiple === false) {
        if (this.filterable && val === null) {
          this.currentVisibled = true
        } else {
          this.currentVisibled = false
        }
      }
    },
    currentVisibled(val) {
      if (val === false) {
        // this.$refs.popperList.unactived()
        if (this.filterable &&
            !this.$listeners.load &&
            this.multiple === false) {
          this.filterValue = this.currentValue ? this.currentValue.label : ''
        }
      }
      this.$emit('update:visibled', val)
    },
    disabled(val) {
      this.currentDisabled = val
    }
  },
  created() {
    this.currentStripe = this.stripe
    this.currentClearable = this.clearable
    this.currentDisabled = this.disabled
    this.currentVisibled = this.visibled
  },
  beforeDestroy() {
    document.body.removeEventListener('keydown', this.$_onDocKeydown)
    this.$_detachFocusEvents()
  },
  mounted() {
    this.currentValue = this.$_normalizeValue(this.value)
    this.currentNode = Array.isArray(this.currentValue) ? this.currentValue.map(v => v.id) : this.currentValue ? [this.currentValue.id] : []
    bus.$on('clearCascader', (selectedComponent, checked) => {
      if (checked) {
        this.currentVisibled = false
        return
      }
      // TODO: 此处的 findComponentUpward 需要替换为事件总线实现!!
      let selectedTCascader = findComponentUpward(selectedComponent, 'TCascader')
      let currentTCascader = findComponentUpward(this, 'TCascader')
      if (selectedTCascader === currentTCascader) { // 级联选择，必须在同一个级联对象下
        this.initFilter = true
        let level = selectedComponent.$parent.level + 1 // 选择第一个不同的数据，它的下一个caspanel会清理掉数据
        if (level === this.$parent.level) {
          if (this.filterable) {
            this.initFilter = false
            this.filterValue = ''
          } else {
            this.currentValue = { }
          }
        }
      }
    })

    this.$_attachFocusEvents()
    document.body.addEventListener('keydown', this.$_onDocKeydown)
  },
  methods: {
    focus () {
      if (this.filterable) {
        this.$refs.input.focus()
      } else {
        this.$refs.wrapper.focus()
      }
    },
    blur () {
      if (this.filterable) {
        this.$refs.input.blur()
      } else {
        this.$refs.wrapper.blur()
      }
    },
    $_changeCurrentVisibled(visibled) {
      if (this.filterable && this.$listeners.load) {
        if (this.filterValue !== '') {
          if (this.currentVisibled) {
            this.$emit('update:loading', false)
          } else {
            // this.$refs.popperList.reset()
            this.treeFilterValue = ''
            this.$emit('update:loading', true)
            this.$emit('load', this.filterValue)
          }
          this.currentVisibled = visibled
        }
      } else {
        this.currentVisibled = visibled
      }
    },
    $_onTriggerClick() {
      if (this.currentDisabled === false) {
        this.$refs.dropdown.updatePosition()
        this.$_changeCurrentVisibled(!this.currentVisibled)
      }
    },
    $_onClickOutside() {
      this.currentVisibled = false
    },
    $_onTriggerMouseenter() {
      if (this.multiple === false &&
          this.currentClearable === true &&
          this.placeholderVisibled === false) {
        this.arrowIconVisibled = false
      }
    },
    $_onTriggerMouseleave() {
      if (this.multiple === false &&
          this.currentClearable === true &&
          this.placeholderVisibled === false) {
        this.arrowIconVisibled = true
      }
    },
    $_onTreeFilter(matched) {
      if (this.multiple === false && matched === FILTER_RESET) {
        this.currentValue = null
      }
      this.matched = matched
    },
    $_treeNodes(selected = []) {
      const nodeObj = this.$refs.tree.treeModel.nodes
      const nodes = Object.keys(nodeObj).map(k => nodeObj[k])
      return selected.length > 0 ? nodes.filter(n => selected.includes(n.id)) : nodes
    },
    $_onTreeSelect(nodeID) {
      const value = this.$_treeNodes(this.currentNode)
      let cloneValue
      if (this.multiple === true) {
        cloneValue = value.concat().map(item => {
          return item.data
        })
      } else {
        cloneValue = value[0].data
        if (this.filterable) {
          this.filterValue = value[0].label
        }
      }
      this.inside = true // 标识从组件内部修改value属性的值
      this.$emit('input', cloneValue)
      this.$emit('update:value', cloneValue)
      this.$emit('change', cloneValue)
      this.$emit('on-select-selected-with-index', nodeID) // 用于级联选择组件
      // NOTE: 用于form验证
      this.dispatch('TFormItem', 'g-on-form-change', cloneValue)
    },
    $_includesValue(val) {
      if ((val === null || val === undefined) || Array.isArray(val)) {
        return null
      }
      const finalValue = (typeof val === 'object') ? val.value : val
      const nodes = this.$refs.tree.treeModel.nodes
      const keys = Object.keys(nodes)
      for (let i = keys.length - 1; i >= 0; i--) {
        const itemValue = nodes[keys[i]].data
        if (itemValue === finalValue) {
          return {
            id: nodes[keys[i]].id,
            value: itemValue,
            label: nodes[keys[i]].label
          }
        }
      }
      return null
    },
    $_normalizeValue(val) {
      if (this.multiple) {
        if (Array.isArray(val)) {
          let finalValue = val.map(item => {
            return this.$_includesValue(item)
          })
          return finalValue
        } else {
          let result = this.$_includesValue(val)
          if (result === null) {
            return []
          } else {
            return [result]
          }
        }
      } else {
        return this.$_includesValue(val)
      }
    },
    $_onInputKeydown() {

    },
    $_onInputDelete() {

    },
    $_onDocKeydown(event) {
      let keyCode = event.keyCode
      if (this.currentVisibled) {
        switch (keyCode) {
          case 27: // Esc取消下拉
            event.preventDefault()
            event.cancelBubble = true
            this.currentVisibled = false
            this.focus()
            break
          case 40: // 下键跳转到下一项
            event.preventDefault()
            event.cancelBubble = true
            // this.$refs.popperList.nextActived()
            break
          case 38: // 上键跳转到上一项
            event.preventDefault()
            event.cancelBubble = true
            // this.$refs.popperList.prevActived()
            break
          case 13: // 回车确定选择
            event.preventDefault()
            event.cancelBubble = true
            // this.$refs.popperList.fireActivedClick()
            if (this.multiple === false) {
              this.focus()
            }
            break
        }
      } else {
        if (keyCode === 40) {
          event.preventDefault()
          event.cancelBubble = true
          if (this.focused && this.currentVisibled === false) {
            this.currentVisibled = true
          }
          // this.$refs.popperList.nextActived()
        }
      }
      event.cancelBubble = true
    },
    $_attachFocusEvents() {
      let el
      if (this.filterable) {
        el = this.$refs.input
      } else {
        el = this.$refs.wrapper
        el.setAttribute('tabIndex', '0')
      }
      el.addEventListener('focus', this.$_onSelectFocus)
      el.addEventListener('blur', this.$_onSelectBlur)
    },
    $_detachFocusEvents() {
      let el
      if (this.filterable) {
        el = this.$refs.input
      } else {
        el = this.$refs.wrapper
        el.removeAttribute('tabindex')
      }
      el.removeEventListener('focus', this.$_onSelectFocus)
      el.removeEventListener('blur', this.$_onSelectBlur)
    },
    $_onSelectFocus() {
      this.$refs.dropdown.resetPrevInstance()
      this.focused = true
    },
    $_onSelectBlur() {
      this.focused = false
    },
    $_onCloseIconClick() {
      if (this.closeIconVisibled) {
        this.filterValue = ''
        this.currentValue = null
        this.arrowIconVisibled = true
        this.inside = true // 标识从组件内部修改value属性的值
        this.$emit('input', null)
        this.$emit('update:value', null)
        this.$emit('change', null)
      }
    },
    $_onRemoveTagClick(item) {
      if (this.currentDisabled === false) {
        if (this.currentVisibled === true) {
          this.currentVisibled = false
        }
        this.currentValue.splice(this.currentValue.indexOf(item), 1)
        let cloneValue
        cloneValue = this.currentValue.concat().map(item => {
          return item
        })
        this.inside = true // 标识从组件内部修改value属性的值
        this.currentNode = cloneValue.map(v => v.id)
        this.$emit('input', cloneValue)
        this.$emit('update:value', cloneValue)
        this.$emit('change', cloneValue)
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/module/select-tree/select-tree.vue